/**
 * SiteMapTab (M6) — MapLibre GL satellite map with English overlay,
 * clickable pins with hover tooltips, animated flow lines, pulsing
 * gate readers, material-code filter, KML/GeoJSON boundary, CCTV
 * deep-links, and per-pin drawer drilldowns. Fills available screen.
 *
 * Layout: outer Warehouse Zone polygon → 2 Bin polygons (Owner /
 * Contractor) inside the zone → per-discipline Rack pins inside each
 * bin. Clicking a rack pin opens the IQC-Accepted ledger drawer for
 * that rack; clicking a bin polygon opens the aggregated bin ledger.
 *
 * Base tiles: ESRI World Imagery (satellite, no key required)
 * Labels overlay: ESRI World Boundaries & Places (transparent, English)
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import api from '@/lib/api';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { RefreshCw, Sparkles, Radio, Warehouse as WhIcon, Layers, Clock,
         Upload, Camera, X, Video, DoorOpen, Search, Map as MapIcon,
         Satellite, Package, Maximize2, Minimize2, Settings, Crosshair } from 'lucide-react';
import SiteMapAdmin from '@/components/mtx/SiteMapAdmin';
import BinTransferDialog from '@/components/mtx/BinTransferDialog';
import IssueMIRFromMap from '@/components/mtx/IssueMIRFromMap';
import RackCapacitySimulator from '@/components/mtx/RackCapacitySimulator';
import { ArrowRightLeft } from 'lucide-react';

const C = { primary: '#1E4E8C', accent: '#F26B1F', slate: '#0F172A',
             slateMuted: '#64748B', border: 'rgba(15,23,42,0.10)',
             success: '#10B981', warn: '#F59E0B', danger: '#EF4444', info: '#3B82F6' };

const WINDOWS = [
  { key: '24h', label: 'Last 24h' },
  { key: '7d',  label: 'Last 7d'  },
  { key: '30d', label: 'Last 30d' },
];

// Discipline chip colors (kept in sync with backend migration)
const DISC_COLOR = {
  PIP: '#0EA5E9', STR: '#8B5CF6', MEC: '#F59E0B',
  ELE: '#EF4444', INS: '#22C55E', CIV: '#94A3B8',
};

function ptFor(idx, offset = 0) {
  const lat = 24.42 + ((idx + offset) % 4) * 0.02;
  const lng = 54.61 + (Math.floor((idx + offset) / 4)) * 0.03;
  return [lng, lat];
}

// ── Base styles ───────────────────────────────────────────────────────────
function satelliteStyle() {
  return {
    version: 8,
    glyphs: 'https://demotiles.maplibre.org/font/{fontstack}/{range}.pbf',
    sources: {
      'sat': { type: 'raster',
        tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'],
        tileSize: 256,
        attribution: 'Tiles © Esri — Source: Esri, Maxar, Earthstar Geographics, and the GIS User Community',
        maxzoom: 19 },
      'labels': { type: 'raster',
        tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}'],
        tileSize: 256, maxzoom: 19 },
    },
    layers: [
      { id: 'sat', type: 'raster', source: 'sat' },
      { id: 'labels', type: 'raster', source: 'labels' },
    ],
  };
}

function voyagerStyle() {
  return {
    version: 8,
    sources: {
      voyager: { type: 'raster',
        tiles: ['https://a.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png',
                'https://b.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png',
                'https://c.basemaps.cartocdn.com/rastertiles/voyager/{z}/{x}/{y}.png'],
        tileSize: 256, attribution: '© OpenStreetMap contributors © CARTO' },
    },
    layers: [{ id: 'voyager', type: 'raster', source: 'voyager' }],
  };
}

function terrainStyle() {
  return {
    version: 8,
    sources: {
      terrain: { type: 'raster',
        tiles: ['https://server.arcgisonline.com/ArcGIS/rest/services/World_Terrain_Base/MapServer/tile/{z}/{y}/{x}'],
        tileSize: 256, attribution: 'Tiles © Esri' },
    },
    layers: [{ id: 'terrain', type: 'raster', source: 'terrain' }],
  };
}

function osmStyle() {
  return {
    version: 8,
    sources: {
      osm: { type: 'raster',
        tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
        tileSize: 256, attribution: '© OpenStreetMap contributors' },
    },
    layers: [{ id: 'osm', type: 'raster', source: 'osm' }],
  };
}

function KpiTile({ label, value, color, testId }) {
  return (
    <div className="p-2 rounded border bg-white flex-1" style={{ borderColor: C.border }} data-testid={testId}>
      <div className="text-[9px] uppercase font-semibold" style={{ color: C.slateMuted }}>{label}</div>
      <div className="text-base font-bold font-mono" style={{ color }}>{value}</div>
    </div>
  );
}

function CognaraPanel({ projectId, refreshKey }) {
  const [ins, setIns] = useState([]);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/v1/projects/${projectId}/mtx/site-map/cognara-insights`);
        if (alive) setIns(data.insights || []);
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, [projectId, refreshKey]);
  const sev = { HIGH: C.danger, MEDIUM: C.warn, INFO: C.info };
  return (
    <div className="sticky top-4 w-64 flex-shrink-0 rounded-lg border p-2 overflow-y-auto"
      style={{ borderColor: C.border, background: 'linear-gradient(180deg, #FEF3C7 0%, #FFFFFF 100%)',
                maxHeight: 'calc(100vh - 200px)' }}
      data-testid="cognara-panel">
      <div className="flex items-center gap-1.5 mb-2">
        <Sparkles className="w-3.5 h-3.5" style={{ color: C.accent }} />
        <div className="text-[10px] uppercase font-bold" style={{ color: C.accent }}>Cognara · Site Map</div>
      </div>
      {ins.length === 0 && (
        <div className="text-[10px] italic" style={{ color: C.slateMuted }}>Loading Cognara analysis…</div>
      )}
      {ins.map((i, idx) => (
        <div key={idx} className="mb-2 p-2 rounded border bg-white" style={{ borderColor: C.border }}>
          <div className="flex items-center gap-1 mb-1">
            <span className="px-1 rounded text-[8.5px] font-bold text-white" style={{ background: sev[i.severity] || C.slateMuted }}>{i.severity}</span>
            <div className="text-[10px] font-bold" style={{ color: C.slate }}>{i.title}</div>
          </div>
          <div className="text-[9px]" style={{ color: C.slateMuted }}>{i.detail}</div>
          <div className="text-[9px] mt-1 italic" style={{ color: C.primary }}>→ {i.action}</div>
        </div>
      ))}
    </div>
  );
}

// ── Drawers ───────────────────────────────────────────────────────────────
// materialFilter (optional) filters the ledger to a single materialCode
// so clicking a sub-cell on the map narrows the drawer view. onIssueMir
// is invoked from the "Issue MIR" button next to each material row.
function MaterialCodeAnalysis({ projectId, location, materialFilter, onIssueMir }) {
  const [rows, setRows] = useState(null);
  const [totals, setTotals] = useState(null);
  const [q, setQ] = useState(materialFilter || '');
  useEffect(() => { setQ(materialFilter || ''); }, [materialFilter]);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get(
          `/v1/projects/${projectId}/mtx/warehouses/${encodeURIComponent(location)}/material-analysis`);
        setRows(data.materialCodes || []); setTotals(data.totals);
      } catch { setRows([]); }
    })();
  }, [projectId, location]);
  if (rows === null) return <div className="text-[10px] italic" style={{ color: C.slateMuted }}>Loading ledger…</div>;
  const filtered = rows.filter(r =>
    !q || r.materialCode?.toLowerCase().includes(q.toLowerCase())
       || r.description?.toLowerCase().includes(q.toLowerCase())
       || r.mciLineNumber?.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="mb-3" data-testid="wh-material-analysis">
      <div className="flex items-center justify-between mb-1">
        <div className="text-[10px] uppercase font-semibold" style={{ color: C.slateMuted }}>
          Material Ledger · {rows.length} codes{materialFilter && <span className="ml-1 px-1 rounded text-white text-[9px]" style={{ background: C.accent }}>filter: {materialFilter}</span>}
        </div>
        <input value={q} onChange={e => setQ(e.target.value)}
          placeholder="Filter"
          className="text-[9px] px-1.5 py-0.5 rounded border w-24"
          style={{ borderColor: C.border }}
          data-testid="wh-analysis-search" />
      </div>
      <MaterialLedgerTable rows={filtered} totals={totals} onIssueMir={onIssueMir} />
    </div>
  );
}

function MaterialLedgerTable({ rows, totals, onIssueMir }) {
  const fmt = (n) => (n === 0 || n == null) ? '—' : (Math.abs(n) >= 1000 ? n.toLocaleString() : n);
  return (
    <div className="rounded border overflow-x-auto" style={{ borderColor: C.border, maxHeight: 320 }}>
      <table className="w-full text-[9px]">
        <thead style={{ background: '#F8FAFC', position: 'sticky', top: 0 }}>
          <tr>
            {['Material · MCI', 'Available (IQC-OK)', 'Issued', 'Final Inv', onIssueMir ? 'MIR' : null].filter(Boolean).map(h => (
              <th key={h} className="text-left p-1 uppercase font-semibold whitespace-nowrap"
                style={{ color: C.slateMuted, fontSize: 8 }}>{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} style={{ borderTop: `1px solid ${C.border}` }} data-testid={`wh-mat-row-${r.materialCode}`}>
              <td className="p-1 font-mono">
                <div className="font-bold" style={{ color: C.primary }}>{r.materialCode}</div>
                <div className="truncate max-w-[140px]" style={{ color: C.slateMuted, fontSize: 8 }}>{r.description}</div>
                <div style={{ color: C.slateMuted, fontSize: 8 }}>{r.mciLineNumber} · {r.unit}</div>
              </td>
              <td className="p-1 font-mono text-right font-bold" style={{ color: C.success }}>{fmt(r.iqcAccepted)}</td>
              <td className="p-1 font-mono text-right" style={{ color: C.accent }}>{fmt(r.issued)}</td>
              <td className="p-1 font-mono text-right font-bold" style={{ color: C.primary }}>{fmt(r.finalInventory)}</td>
              {onIssueMir && (
                <td className="p-1 text-right">
                  <button onClick={() => onIssueMir(r)} data-testid={`wh-mat-mir-${r.materialCode}`}
                    className="px-1.5 py-0.5 rounded text-white text-[8.5px] font-bold"
                    style={{ background: C.accent }}
                    disabled={(r.finalInventory ?? r.iqcAccepted) <= 0}>
                    Issue MIR
                  </button>
                </td>
              )}
            </tr>
          ))}
          {rows.length === 0 && (
            <tr><td colSpan={onIssueMir ? 5 : 4} className="p-3 text-center italic" style={{ color: C.slateMuted }}>No matching material codes</td></tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

// New: when a BIN is clicked (aggregated bin drawer), we render one
// collapsible section per child rack — inside each, the same
// MaterialLedgerTable but scoped to that rack. This gives the user the
// Warehouse > Rack > Material hierarchy they asked for.
function BinRackAnalysis({ projectId, racks, onIssueMir }) {
  const [byRack, setByRack] = useState({});
  const [open, setOpen] = useState({});
  const [rackOverrides, setRackOverrides] = useState({});   // rackId → new rackCapacity (post-save)
  useEffect(() => {
    (async () => {
      const out = {};
      await Promise.all(racks.map(async (r) => {
        try {
          const { data } = await api.get(
            `/v1/projects/${projectId}/mtx/warehouses/${encodeURIComponent(r.location)}/material-analysis`);
          out[r.location] = { rows: data.materialCodes || [], totals: data.totals || {} };
        } catch { out[r.location] = { rows: [], totals: {} }; }
      }));
      setByRack(out);
      if (racks.length) setOpen({ [racks[0].location]: true });
    })();
  }, [projectId, racks.map(r => r.location).join('|')]); // eslint-disable-line
  return (
    <div className="space-y-2" data-testid="wh-bin-rack-analysis">
      {racks.map(r => {
        const data = byRack[r.location];
        const rows = data?.rows || [];
        const totals = data?.totals || {};
        const isOpen = !!open[r.location];
        const fill = r.fillPct ?? 0;
        const fillColor = r.fillStatus === 'DANGER' ? C.danger
                        : r.fillStatus === 'WARN'   ? C.warn : C.success;
        return (
          <div key={r.location} className="rounded-lg overflow-hidden border"
            style={{ borderColor: r.disciplineColor + '55', background: '#fff' }}
            data-testid={`bin-rack-block-${r.disciplineCode}`}>
            <button onClick={() => setOpen({ ...open, [r.location]: !isOpen })}
              className="w-full px-2 py-1.5 flex items-center gap-2 text-left"
              style={{ background: r.disciplineColor + '11', borderBottom: isOpen ? `1px solid ${r.disciplineColor}22` : 'none' }}>
              <span style={{ display:'inline-block',width:10,height:10,background:r.disciplineColor,borderRadius:2 }} />
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-bold" style={{ color: r.disciplineColor }}>{r.location}</div>
                <div className="flex items-center gap-1.5 mt-0.5">
                  <div className="flex-1 h-1 rounded-full overflow-hidden" style={{ background: '#E2E8F0', maxWidth: 140 }}>
                    <div className="h-full" style={{ width: `${Math.min(100, fill)}%`, background: fillColor }} />
                  </div>
                  <span className="text-[8.5px] font-mono font-bold tabular-nums" style={{ color: fillColor }}>{fill.toFixed(0)}%</span>
                </div>
              </div>
              <span className="text-[10px]" style={{ color: C.slateMuted }}>{isOpen ? '▾' : '▸'}</span>
            </button>
            {isOpen && (
              <div className="p-2 space-y-2">
                {/* Capacity What-If simulator — planner can drag the
                    capacity slider and preview OK/WARN/DANGER against the
                    current live stock, then save the new capacity. */}
                <RackCapacitySimulator
                  projectId={projectId}
                  rack={{ ...r, rackCapacity: rackOverrides[r.id] ?? r.rackCapacity }}
                  currentStock={Number(totals?.currentBalance ?? totals?.totalAcceptedQty ?? 0)}
                  onSaved={(nr) => setRackOverrides(prev => ({ ...prev, [nr.id]: nr.rackCapacity }))}
                />
                {!data && <div className="text-[9.5px] italic" style={{ color: C.slateMuted }}>Loading…</div>}
                {data && rows.length === 0 && (
                  <div className="text-[9.5px] italic py-2 text-center" style={{ color: C.slateMuted }}>No stock on this rack yet.</div>
                )}
                {data && rows.length > 0 && <MaterialLedgerTable rows={rows} totals={totals} onIssueMir={onIssueMir} />}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

function WarehouseDrawer({ projectId, location, subtitle, highlightUnit, onTransfer, allWarehouses = [], materialFilter, onIssueMir, onClose }) {
  const [d, setD] = useState(null);
  const [cctv, setCctv] = useState([]);
  // A location is a BIN if allWarehouses has a matching w.type === 'BIN' row
  const wh = allWarehouses.find(w => w.location === location);
  const isBin = wh?.type === 'BIN';
  const childRacks = isBin
    ? allWarehouses.filter(w => w.type === 'RACK'
        && (w.parentBin === wh.id || w.parentBinCode === wh.location?.split(' ')[0]
            || (w.parentBinCode === 'OWN-BIN' && location.includes('Owner'))
            || (w.parentBinCode === 'CTR-BIN' && location.includes('Contractor'))))
    : [];
  useEffect(() => {
    if (!location) return;
    (async () => {
      try {
        const [{ data: dd }, { data: cc }] = await Promise.all([
          api.get(`/v1/projects/${projectId}/mtx/warehouses/${encodeURIComponent(location)}/drilldown`),
          api.get(`/v1/projects/${projectId}/mtx/cctv-links?scopeKey=${encodeURIComponent(location)}`),
        ]);
        setD(dd); setCctv(cc.links || []);
      } catch { setD({ totalIqcOkStock: 0, distinctLines: 0, bins: [], recentScans: [], topLines: [], unitStates: {} }); }
    })();
  }, [projectId, location]);
  if (!location) return null;
  // Non-modal side panel: no full-inset backdrop → the MapLibre canvas
  // under the un-overlapped portion of the screen stays fully
  // interactive (pan / zoom / hover / click on other racks and bins).
  return (
    <div className="fixed top-16 right-3 bottom-3 w-[440px] z-30 rounded-lg pointer-events-auto"
         style={{ background: '#fff', boxShadow: '0 20px 40px rgba(15,23,42,0.20), 0 0 0 1px rgba(15,23,42,0.06)' }}
         data-testid="warehouse-drawer">
      <div className="h-full overflow-y-auto p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2">
              <WhIcon className="w-4 h-4" style={{ color: C.success }} />
              <div className="text-sm font-bold" style={{ color: C.slate }}>{location}</div>
            </div>
            <div className="text-[10px]" style={{ color: C.slateMuted }}>{subtitle || 'IQC-Accepted Material Ledger'}</div>
          </div>
          <div className="flex items-center gap-1">
            {onTransfer && (
              <button onClick={() => onTransfer(location)}
                className="px-2 py-1 rounded text-[10px] font-bold flex items-center gap-1"
                style={{ background: C.accent, color: '#fff' }}
                data-testid="wh-drawer-transfer">
                <ArrowRightLeft className="w-3 h-3" /> Transfer
              </button>
            )}
            <button onClick={onClose} data-testid="wh-drawer-close"><X className="w-4 h-4" /></button>
          </div>
        </div>
        {!d && <div className="text-[11px] italic" style={{ color: C.slateMuted }}>Loading…</div>}
        {d && (
          <>
            {highlightUnit && (
              <div className="p-2 mb-3 rounded" style={{ background: '#FEF3C7', border: '1px solid #FCD34D' }}>
                <div className="text-[10px] uppercase font-bold" style={{ color: '#92400E' }}>Filter Match</div>
                <div className="text-[11px] font-mono font-bold">{highlightUnit.id}</div>
                <div className="text-[9px]" style={{ color: '#92400E' }}>{highlightUnit.materialDescription}</div>
                <div className="text-[9px]" style={{ color: '#92400E' }}>State: {highlightUnit.currentState} · Bin: {highlightUnit.currentBin || '—'}</div>
              </div>
            )}
            <div className="grid grid-cols-2 gap-2 mb-3">
              <KpiTile testId="wh-drawer-stock" label="IQC-OK Stock" value={d.totalIqcOkStock} color={C.primary} />
              <KpiTile testId="wh-drawer-lines" label="Distinct Lines" value={d.distinctLines} color={C.accent} />
            </div>
            {isBin && childRacks.length > 0 ? (
              <BinRackAnalysis projectId={projectId} racks={childRacks} onIssueMir={onIssueMir} />
            ) : (
              <MaterialCodeAnalysis projectId={projectId} location={location}
                materialFilter={materialFilter} onIssueMir={onIssueMir} />
            )}            <div className="mb-3">
              <div className="text-[10px] uppercase font-semibold mb-1" style={{ color: C.slateMuted }}>Unit State Histogram</div>
              <div className="flex flex-wrap gap-1">
                {Object.entries(d.unitStates || {}).map(([st, ct]) => (
                  <span key={st} className="px-1.5 py-0.5 rounded text-[9px] font-mono"
                    style={{ background: '#EEF2FF', color: C.primary }}>
                    {st.replace(/_/g,' ')}: <b>{ct}</b>
                  </span>
                ))}
              </div>
            </div>
            {(d.topLines || []).length > 0 && (
              <div className="mb-3">
                <div className="text-[10px] uppercase font-semibold mb-1" style={{ color: C.slateMuted }}>Top-Value Lines</div>
                {d.topLines.map((l, i) => (
                  <div key={i} className="text-[10px] p-1.5 border-b" style={{ borderColor: C.border }}>
                    <div className="font-mono font-bold">{l.mciLineNumber}</div>
                    <div className="truncate" style={{ color: C.slateMuted }}>{l.description}</div>
                    <div className="text-[9px]" style={{ color: C.primary }}>{l.acceptedQty} / {l.orderedQty}</div>
                  </div>
                ))}
              </div>
            )}
            {cctv.length > 0 && (
              <div className="mb-3">
                <div className="text-[10px] uppercase font-semibold mb-1 flex items-center gap-1" style={{ color: C.slateMuted }}>
                  <Video className="w-3 h-3" /> CCTV Feeds
                </div>
                {cctv.map((c, i) => (
                  <a key={i} href={c.url} target="_blank" rel="noreferrer"
                    className="block text-[10px] p-1.5 rounded border mb-1 hover:bg-slate-50"
                    style={{ borderColor: C.border, color: C.info }}>{c.label} ↗</a>
                ))}
              </div>
            )}
            <div className="mb-3">
              <div className="text-[10px] uppercase font-semibold mb-1" style={{ color: C.slateMuted }}>Recent Scans</div>
              {(d.recentScans || []).slice(0, 8).map((r, i) => (
                <div key={i} className="text-[9px] p-1 border-b" style={{ borderColor: C.border }}>
                  <span className="font-mono font-bold">{r.interfacePointType}</span> · {r.materialUnitId?.slice(-6)} ·
                  <span style={{ color: C.slateMuted }}> {new Date(r.scannedAt).toLocaleString()}</span>
                </div>
              ))}
              {(!d.recentScans || d.recentScans.length === 0) && (
                <div className="text-[10px] italic" style={{ color: C.slateMuted }}>No recent scans</div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function GateReaderDrawer({ projectId, ipId, onClose }) {
  const [d, setD] = useState(null);
  const [triggering, setTriggering] = useState(false);
  useEffect(() => {
    if (!ipId) return;
    (async () => {
      const { data } = await api.get(`/v1/projects/${projectId}/mtx/interface-points/${ipId}/recent-scans`);
      setD(data);
    })();
  }, [projectId, ipId]);
  const trigger = async () => {
    setTriggering(true);
    try {
      const { data } = await api.post(`/v1/projects/${projectId}/mtx/gate-barrier/${ipId}/trigger`);
      if (data.ok) toast.success('Gate barrier fired');
      else toast.error(`Barrier failed: ${data.error || data.status}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Barrier webhook not configured');
    } finally { setTriggering(false); }
  };
  if (!ipId) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-stretch justify-end" style={{ background: 'rgba(15,23,42,0.35)' }} onClick={onClose}>
      <div className="w-[400px] bg-white shadow-2xl overflow-y-auto p-4" onClick={e => e.stopPropagation()} data-testid="gate-reader-drawer">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2">
              <Radio className="w-4 h-4" style={{ color: C.info }} />
              <div className="text-sm font-bold" style={{ color: C.slate }}>{d?.interfacePoint?.label || ipId}</div>
            </div>
            <div className="text-[10px]" style={{ color: C.slateMuted }}>{d?.interfacePoint?.type} · Status: {d?.interfacePoint?.status}</div>
          </div>
          <button onClick={onClose} data-testid="gate-drawer-close"><X className="w-4 h-4" /></button>
        </div>
        <Button size="sm" onClick={trigger} disabled={triggering}
          data-testid="gate-barrier-trigger"
          className="w-full mb-3" style={{ background: C.accent, color: '#fff' }}>
          <DoorOpen className="w-3.5 h-3.5 mr-1" /> {triggering ? 'Firing…' : 'Open Gate Barrier'}
        </Button>
        <div className="mb-2 text-[10px] uppercase font-semibold" style={{ color: C.slateMuted }}>Last {(d?.events || []).length} Scans</div>
        {(d?.events || []).map((e, i) => (
          <div key={i} className="p-2 mb-1 rounded border text-[10px]" style={{ borderColor: C.border }}>
            <div className="flex justify-between">
              <span className="font-mono font-bold">{e.unit?.materialCode || e.materialUnitId?.slice(-8)}</span>
              <span style={{ color: C.slateMuted }}>{new Date(e.scannedAt).toLocaleString()}</span>
            </div>
            <div className="truncate" style={{ color: C.slateMuted }}>{e.unit?.materialDescription}</div>
            <div className="text-[9px]" style={{ color: C.primary }}>{e.previousState} → {e.newState} · {e.scannedBy}</div>
          </div>
        ))}
        {d && (d.events || []).length === 0 && (
          <div className="text-[10px] italic" style={{ color: C.slateMuted }}>No scans yet</div>
        )}
      </div>
    </div>
  );
}

function HealthCheckBanner({ projectId, refreshKey }) {
  const [check, setCheck] = useState(null);
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/v1/projects/${projectId}/mtx/site-map/health-check`);
        if (alive) setCheck(data);
      } catch { /* silent */ }
    })();
    return () => { alive = false; };
  }, [projectId, refreshKey]);
  if (!check) return null;
  if (check.ok) {
    return (
      <div className="px-2 py-1 rounded text-[10px] flex items-center gap-2"
        style={{ background: '#D1FAE5', color: '#065F46' }}
        data-testid="sm-health-ok">
        <span className="font-bold">✓ Reconciled with Inventory</span>
        <span>· {check.warehousesChecked} warehouses checked · zero drift</span>
      </div>
    );
  }
  return (
    <div className="px-2 py-1.5 rounded text-[10px] border-l-4"
      style={{ background: '#FEE2E2', color: '#991B1B', borderColor: '#EF4444' }}
      data-testid="sm-health-drift">
      <div className="font-bold mb-0.5">⚠ Inventory drift detected — {check.drifts.length} warehouse(s) out of sync</div>
      {check.drifts.slice(0, 5).map((d, i) => (
        <div key={i} className="text-[9.5px] font-mono">
          {d.location}: Inventory=<b>{d.inventoryStock}</b> · Site Map=<b>{d.siteMapStock}</b> · drift <b>{d.driftPct}%</b> (Δ {d.delta})
        </div>
      ))}
      {check.drifts.length > 5 && <div className="text-[9px] italic">+{check.drifts.length - 5} more</div>}
    </div>
  );
}

export default function SiteMapTab({ projectId }) {
  const [win, setWin] = useState('24h');
  const [snap, setSnap] = useState(null);
  const [baseStyle, setBaseStyle] = useState('satellite');
  const [layers, setLayers] = useState({
    binPolygons: true, rackPins: true, gateReaders: true, workfronts: true,
    coverageRings: true,
  });
  const [refreshKey, setRefreshKey] = useState(0);
  const [whDrawer, setWhDrawer] = useState(null);
  const [drawerSubtitle, setDrawerSubtitle] = useState(null);
  const [highlightUnit, setHighlightUnit] = useState(null);
  const [gateDrawer, setGateDrawer] = useState(null);
  const [boundary, setBoundary] = useState(null);
  const [materialFilter, setMaterialFilter] = useState('');
  const [filterMatches, setFilterMatches] = useState([]);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [adminOpen, setAdminOpen] = useState(false);
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferFromLoc, setTransferFromLoc] = useState('');
  // Material sub-cell drilldown state — when a rack is focused, its
  // material codes are rendered as an equal-sized grid inside the rack
  // polygon. Clicking a cell filters the drawer to that material.
  const [focusedRack, setFocusedRack] = useState(null);          // rack `location`
  const [rackMaterials, setRackMaterials] = useState([]);        // materialCodes array
  const [focusedMaterial, setFocusedMaterial] = useState(null); // filter drawer to this materialCode
  const [issueMirFor, setIssueMirFor] = useState(null);          // {warehouseLocation, material}
  const [activeCategories, setActiveCategories] = useState([]);  // legend chips for material sub-cells
  const initialFitDoneRef = useRef(false);   // prevents fitBounds from firing on every render (zoom-flicker bug)
  const [pickGpsCb, setPickGpsCb] = useState(null);
  const mapRef = useRef(null);
  const containerRef = useRef(null);
  const fileRef = useRef(null);
  const popupRef = useRef(null);
  const dashRafRef = useRef(0);
  // ── Rack-hang guard ───────────────────────────────────────────────
  // Every re-run of the main render effect used to call `map.on(...)`
  // for click / mouseenter / mouseleave on every layer, WITHOUT
  // detaching the previous handler.  Because MapLibre keeps attached
  // handlers on the Map (not on the removed layer), each rack click
  // triggered `setFocusedRack` + `setRackMaterials` → effect re-runs →
  // TWO extra 'racks-fill' click handlers get bound → the next click
  // fires all of them → state cascades exponentially → the tab hangs.
  // We now register every handler through `bindHandler` which stores
  // it in `handlersRef` and detaches the previous entry before adding
  // the new one.
  const handlersRef = useRef([]);

  const load = useCallback(async () => {
    if (!projectId) return;
    try {
      const [{ data: sn }, { data: bd }] = await Promise.all([
        api.get(`/v1/projects/${projectId}/mtx/site-map/snapshot?window=${win}`),
        api.get(`/v1/projects/${projectId}/mtx/site-boundary`),
      ]);
      setSnap(sn); setBoundary(bd);
    } catch { /* silent */ }
  }, [projectId, win]);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (!projectId || !materialFilter || materialFilter.length < 2) {
      setFilterMatches([]); return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get(
          `/v1/projects/${projectId}/mtx/material-units/lookup?q=${encodeURIComponent(materialFilter)}`);
        if (data.match) setFilterMatches([data.match]);
        else setFilterMatches((data.candidates || []).slice(0, 6));
      } catch { setFilterMatches([]); }
    }, 320);
    return () => clearTimeout(t);
  }, [projectId, materialFilter]);

  // Initialise MapLibre
  useEffect(() => {
    if (!containerRef.current) return;
    if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: baseStyle === 'satellite' ? satelliteStyle()
            : baseStyle === 'voyager'  ? voyagerStyle()
            : baseStyle === 'terrain'  ? terrainStyle()
            : osmStyle(),
      center: [52.6510, 24.1155], zoom: 16,
    });
    map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), 'top-right');
    map.addControl(new maplibregl.ScaleControl({ maxWidth: 120, unit: 'metric' }), 'bottom-left');
    map.addControl(new maplibregl.FullscreenControl(), 'top-right');
    map.on('load', () => { mapRef.current = map; setRefreshKey(k => k + 1); });
    return () => { map.remove(); mapRef.current = null;
                    if (dashRafRef.current) clearTimeout(dashRafRef.current); };
  }, [baseStyle]);

  // Render zone + bins + racks + gates + flows
  useEffect(() => {
    const map = mapRef.current;
    if (!map || !snap) return;
    // Retry when the style finishes loading — silently returning here
    // used to leave the map blank when snap arrived before the
    // MapLibre style was fully hydrated.
    if (!map.isStyleLoaded()) {
      const retry = () => setRefreshKey(k => k + 1);
      map.once('idle', retry);
      return () => { try { map.off('idle', retry); } catch {} };
    }
    if (dashRafRef.current) clearTimeout(dashRafRef.current);
    // Detach every handler bound on the previous render pass so a rack
    // click never fires two (or more) stale copies of the same
    // callback (see comment on `handlersRef` for full context).
    handlersRef.current.forEach(({ event, layer, handler }) => {
      try { if (layer) map.off(event, layer, handler); else map.off(event, handler); } catch {}
    });
    handlersRef.current = [];
    const bindHandler = (event, layer, handler) => {
      if (layer) map.on(event, layer, handler); else map.on(event, handler);
      handlersRef.current.push({ event, layer, handler });
    };
    // Wipe previous layers/sources
    ['zone-fill', 'zone-line', 'bins-fill', 'bins-line', 'bins-label',
     'stations-fill', 'stations-line', 'stations-label',
     'racks-fill', 'racks-line', 'racks-label',
     'mat-cells-fill', 'mat-cells-line', 'mat-cells-label',
     'gate-pulse', 'gate-src',
     'convoy-glow', 'convoy-line',
     'coverage-rings-fill', 'coverage-rings-line'].forEach(id => {
      if (map.getLayer(id)) map.removeLayer(id);
    });
    ['zone-src', 'bins-src', 'stations-src', 'racks-src', 'racks-labels-src',
     'mat-cells-src', 'mat-cells-labels-src',
     'convoy-src',
     'gate-src', 'coverage-rings']
      .forEach(id => { if (map.getSource(id)) map.removeSource(id); });

    // ── 1. Outer Warehouse Zone polygon ──────────────────────────────
    let zoneGeo = null;
    if (boundary?.format === 'geojson' && boundary.payload && layers.workfronts) {
      try { zoneGeo = JSON.parse(boundary.payload); } catch {}
    }
    if (zoneGeo) {
      map.addSource('zone-src', { type: 'geojson', data: zoneGeo });
      map.addLayer({ id: 'zone-fill', type: 'fill', source: 'zone-src', paint: {
        'fill-color': C.accent, 'fill-opacity': 0.06,
      } });
      map.addLayer({ id: 'zone-line', type: 'line', source: 'zone-src', paint: {
        'line-color': C.accent, 'line-width': 2, 'line-dasharray': [4, 2],
      } });
      // Auto-fit to zone bounds
      try {
        const collect = (coords, out) => {
          if (typeof coords[0] === 'number') { out.push(coords); return; }
          coords.forEach(c => collect(c, out));
        };
        const pts = [];
        (zoneGeo.features || [zoneGeo]).forEach(feat => {
          const g = feat.geometry || feat;
          if (g?.coordinates) collect(g.coordinates, pts);
        });
        if (pts.length >= 2 && !initialFitDoneRef.current) {
          const lngs = pts.map(p => p[0]), lats = pts.map(p => p[1]);
          map.fitBounds([[Math.min(...lngs), Math.min(...lats)],
                          [Math.max(...lngs), Math.max(...lats)]],
                         { padding: 80, duration: 900 });
          initialFitDoneRef.current = true;
        }
      } catch {}
    }

    // ── 2. Bin polygons (Owner / Contractor) ──────────────────────────
    const bins = (snap.warehouses || []).filter(w => w.type === 'BIN' && w.polygon);
    if (layers.binPolygons && bins.length) {
      const feats = bins.map(w => ({
        type: 'Feature',
        properties: { id: w.id, location: w.location, label: w.label, stock: w.iqcOkStock || 0,
                       balance: w.balanceStock ?? w.iqcOkStock ?? 0, health: w.health,
                       isOwner: w.location?.includes('Owner') },
        geometry: w.polygon,
      }));
      map.addSource('bins-src', { type: 'geojson',
        data: { type: 'FeatureCollection', features: feats } });
      map.addLayer({ id: 'bins-fill', type: 'fill', source: 'bins-src', paint: {
        'fill-color': ['case', ['get', 'isOwner'], '#3B82F6', '#8B5CF6'],
        'fill-opacity': 0.20,
      } });
      map.addLayer({ id: 'bins-line', type: 'line', source: 'bins-src', paint: {
        'line-color': ['case', ['get', 'isOwner'], '#1E40AF', '#6D28D9'],
        'line-width': 2.5, 'line-opacity': 0.9,
      } });
      map.addLayer({ id: 'bins-label', type: 'symbol', source: 'bins-src',
        layout: {
          'text-field': ['format',
            ['get', 'label'], { 'font-scale': 1.0 },
            '\n', {},
            ['concat', 'Stock: ', ['to-string', ['get', 'balance']]], { 'font-scale': 0.75 },
          ],
          'text-size': 12,
          'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
          'text-anchor': 'top',
          'text-offset': [0, -8],
          'text-allow-overlap': true,
        },
        paint: { 'text-color': '#0F172A', 'text-halo-color': '#FFFFFF', 'text-halo-width': 2.2 },
      });
    }

    // ── 2b. Site Station polygons — on-site inventory yards ──────────
    // Rendered in teal (accent) to distinguish from warehouse bins.
    // Same click-to-open-drawer + hover-popup behaviour.
    const stations = (snap.warehouses || []).filter(w => w.type === 'SITE_STATION' && w.polygon);
    if (layers.binPolygons && stations.length) {
      const feats = stations.map(w => ({
        type: 'Feature',
        properties: { id: w.id, location: w.location, label: w.label,
                       stock: w.iqcOkStock || 0, balance: w.balanceStock ?? 0 },
        geometry: w.polygon,
      }));
      map.addSource('stations-src', { type: 'geojson',
        data: { type: 'FeatureCollection', features: feats } });
      map.addLayer({ id: 'stations-fill', type: 'fill', source: 'stations-src', paint: {
        'fill-color': '#14B8A6', 'fill-opacity': 0.20,
      } });
      map.addLayer({ id: 'stations-line', type: 'line', source: 'stations-src', paint: {
        'line-color': '#0F766E', 'line-width': 2.5, 'line-opacity': 0.9,
      } });
      map.addLayer({ id: 'stations-label', type: 'symbol', source: 'stations-src',
        layout: {
          'text-field': ['format',
            ['get', 'label'], { 'font-scale': 1.0 },
            '\n', {},
            ['concat', 'On-site · ', ['to-string', ['get', 'balance']], ' units'], { 'font-scale': 0.7 },
          ],
          'text-size': 12,
          'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'],
          'text-anchor': 'top',
          'text-offset': [0, -8],
          'text-allow-overlap': true,
        },
        paint: { 'text-color': '#0F172A', 'text-halo-color': '#FFFFFF', 'text-halo-width': 2.2 },
      });
    }

    // ── 3. Rack polygons — sized squares INSIDE bin polygons ─────────
    // Renders each rack as a rounded rectangle whose footprint scales
    // with rackCapacity (biggest rack = biggest box). Only warehouse
    // entry/exit points (gate readers) stay as dots.
    const racks = (snap.warehouses || []).filter(w => w.type === 'RACK' || w.type === 'STATION_RACK');
    if (layers.rackPins && racks.length) {
      const maxCap = Math.max(1, ...racks.map(w => Number(w.rackCapacity) || 1));
      const feats = racks.map(w => {
        const cap = Number(w.rackCapacity) || 1;
        // 30m base + up to 60m more depending on capacity share
        const sizeM = 30 + (cap / maxCap) * 60;
        const dLat = (sizeM / 2) / 111320;
        const dLng = (sizeM / 2) / (111320 * Math.cos((w.gpsLat * Math.PI) / 180));
        const poly = { type: 'Polygon', coordinates: [[
          [w.gpsLng - dLng, w.gpsLat - dLat],
          [w.gpsLng + dLng, w.gpsLat - dLat],
          [w.gpsLng + dLng, w.gpsLat + dLat],
          [w.gpsLng - dLng, w.gpsLat + dLat],
          [w.gpsLng - dLng, w.gpsLat - dLat],
        ]]};
        return {
          type: 'Feature',
          properties: {
            id: w.id, location: w.location, label: w.label,
            discipline: w.discipline, disciplineCode: w.disciplineCode,
            color: w.disciplineColor || DISC_COLOR[w.disciplineCode] || C.slateMuted,
            stock: w.iqcOkStock || 0,
            balance: w.balanceStock ?? w.iqcOkStock ?? 0,
            fillPct: w.fillPct ?? 0,
            fillStatus: w.fillStatus || 'OK',
            rackCapacity: w.rackCapacity ?? 0,
            capacityUnit: w.capacityUnit || '',
            parentBin: w.parentBinCode,
            _labelLng: w.gpsLng, _labelLat: w.gpsLat,
          },
          geometry: poly,
        };
      });
      // A second point-feature layer for label anchoring (polygon labels
      // don't center as cleanly on tiny footprints).
      const labelFeats = racks.map(w => ({
        type: 'Feature',
        properties: {
          disciplineCode: w.disciplineCode,
          color: w.disciplineColor || DISC_COLOR[w.disciplineCode] || C.slateMuted,
        },
        geometry: { type: 'Point', coordinates: [w.gpsLng, w.gpsLat] },
      }));
      map.addSource('racks-src', { type: 'geojson',
        data: { type: 'FeatureCollection', features: feats } });
      map.addSource('racks-labels-src', { type: 'geojson',
        data: { type: 'FeatureCollection', features: labelFeats } });
      map.addLayer({ id: 'racks-fill', type: 'fill', source: 'racks-src', paint: {
        'fill-color': ['get', 'color'],
        'fill-opacity': 0.75,
      } });
      map.addLayer({ id: 'racks-line', type: 'line', source: 'racks-src', paint: {
        'line-color': '#0F172A',
        'line-width': 1.2,
        'line-opacity': 0.9,
      } });
      map.addLayer({ id: 'racks-label', type: 'symbol', source: 'racks-labels-src',
        layout: { 'text-field': ['get', 'disciplineCode'], 'text-size': 11,
                    'text-anchor': 'center', 'text-allow-overlap': true,
                    'text-font': ['Open Sans Regular', 'Arial Unicode MS Regular'] },
        paint: { 'text-color': '#fff', 'text-halo-color': '#0F172A', 'text-halo-width': 1.4 },
      });
    }

    // ── 4. Gate readers ───────────────────────────────────────────────
    if (layers.gateReaders && snap.gateReaders?.length) {
      const feats = snap.gateReaders.map((g, i) => ({
        type: 'Feature',
        properties: { id: g.id, type: g.type, status: g.status, label: g.label, scans: g.scansInWindow },
        geometry: { type: 'Point', coordinates: [g.gpsLng ?? ptFor(i, 100)[0],
                                                    g.gpsLat ?? ptFor(i, 100)[1]] },
      }));
      map.addSource('gate-src', { type: 'geojson', data: { type: 'FeatureCollection', features: feats } });
      map.addLayer({ id: 'gate-pulse', type: 'circle', source: 'gate-src', paint: {
        'circle-radius': 14,
        'circle-color': ['case',
          ['==', ['get', 'status'], 'ONLINE'], C.success,
          ['==', ['get', 'status'], 'MISSING'], C.danger, C.warn],
        'circle-opacity': 0.15, 'circle-blur': 0.6,
      } });
      map.addLayer({ id: 'gate-src', type: 'circle', source: 'gate-src', paint: {
        'circle-radius': 7,
        'circle-color': ['case',
          ['==', ['get', 'status'], 'ONLINE'], C.success,
          ['==', ['get', 'status'], 'MISSING'], C.danger, C.warn],
        'circle-stroke-color': '#fff', 'circle-stroke-width': 2,
      } });
    }

    // ── In-Transit Convoy Routes ─────────────────────────────────────
    // Renders an animated dashed polyline between each in-transit MIR's
    // source warehouse centroid and destination station centroid so
    // logistics/planners see live convoys crossing the site.
    if (snap.inTransitRoutes?.length) {
      const routeFeats = snap.inTransitRoutes
        .filter(r => r.fromCoord && r.toCoord)
        .map(r => ({
          type: 'Feature',
          properties: { mirId: r.mirId, code: r.mirCode, gpId: r.gatePassId, status: r.status },
          geometry: { type: 'LineString', coordinates: [r.fromCoord, r.toCoord] },
        }));
      if (routeFeats.length) {
        map.addSource('convoy-src', { type: 'geojson', data: { type: 'FeatureCollection', features: routeFeats } });
        map.addLayer({ id: 'convoy-glow', type: 'line', source: 'convoy-src', paint: {
          'line-color': '#F59E0B', 'line-width': 6, 'line-opacity': 0.18, 'line-blur': 2,
        } });
        map.addLayer({ id: 'convoy-line', type: 'line', source: 'convoy-src', paint: {
          'line-color': '#F59E0B', 'line-width': 2.5, 'line-dasharray': [3, 3],
        } });
        // Animated dashed offset — cycles through 20 frames so trucks
        // appear to be moving even on a static basemap.
        let step = 0;
        if (dashRafRef.current) clearTimeout(dashRafRef.current);
        const animate = () => {
          step = (step + 1) % 20;
          const dash = [3, 3, 3 - (step / 6.66), 3 + (step / 6.66)];
          if (map.getLayer('convoy-line')) {
            try { map.setPaintProperty('convoy-line', 'line-dasharray', dash); } catch { /* map disposed */ }
          }
          dashRafRef.current = setTimeout(animate, 90);
        };
        animate();
      }
    }

    // ── 4. Material sub-cells INSIDE the focused rack ────────────────
    // When a user clicks a rack, we render an equal-sized grid of
    // material-code cells inside its polygon. Each cell is clickable to
    // filter the drawer ledger to just that material code.
    if (focusedRack && rackMaterials.length > 0) {
      const rack = racks.find(w => w.location === focusedRack);
      if (rack) {
        // Consolidate duplicates: multiple MCI lines with the same
        // materialCode collapse into a single sub-cell showing the
        // summed stock and a "N POs" badge.
        const dedupMap = new Map();
        rackMaterials.forEach(m => {
          const key = m.materialCode || m.description;
          if (!dedupMap.has(key)) {
            dedupMap.set(key, {
              materialCode: m.materialCode,
              description: m.description,
              unit: m.unit,
              category: m.category,
              stock: 0,
              poLines: [],
              contributingMcis: [],
            });
          }
          const agg = dedupMap.get(key);
          agg.stock += (m.finalInventory ?? m.iqcAccepted ?? 0);
          agg.poLines.push(m.poNumber || m.mciLineNumber);
          agg.contributingMcis.push(m.mciLineNumber);
        });
        const dedup = Array.from(dedupMap.values());
        const cap = Number(rack.rackCapacity) || 1;
        const sizeM = 30 + (cap / Math.max(1, ...racks.map(w => Number(w.rackCapacity) || 1))) * 60;
        const dLat = (sizeM / 2) / 111320;
        const dLng = (sizeM / 2) / (111320 * Math.cos((rack.gpsLat * Math.PI) / 180));
        const n = dedup.length;
        const cols = Math.ceil(Math.sqrt(n));
        const rows = Math.ceil(n / cols);
        const cellDLat = dLat * 2 / rows;
        const cellDLng = dLng * 2 / cols;
        const padLat = cellDLat * 0.10, padLng = cellDLng * 0.10;
        const catColor = { PIPING: '#0EA5E9', STRUCTURAL: '#8B5CF6', MECHANICAL: '#F59E0B',
                            ELECTRICAL: '#EF4444', INSTRUMENTATION: '#22C55E', CIVIL: '#94A3B8' };
        const feats = dedup.map((m, i) => {
          const r = Math.floor(i / cols), c = i % cols;
          const lngMin = rack.gpsLng - dLng + c * cellDLng + padLng;
          const lngMax = rack.gpsLng - dLng + (c + 1) * cellDLng - padLng;
          const latMax = rack.gpsLat + dLat - r * cellDLat - padLat;
          const latMin = rack.gpsLat + dLat - (r + 1) * cellDLat + padLat;
          return {
            type: 'Feature',
            properties: {
              materialCode: m.materialCode,
              description: m.description, unit: m.unit,
              stock: m.stock,
              poCount: m.poLines.length,
              category: m.category || '',
              color: catColor[(m.category || '').toUpperCase()] || rack.disciplineColor || '#94A3B8',
            },
            geometry: { type: 'Polygon', coordinates: [[
              [lngMin, latMin], [lngMax, latMin],
              [lngMax, latMax], [lngMin, latMax], [lngMin, latMin]]] },
          };
        });
        map.addSource('mat-cells-src', { type: 'geojson', data: { type: 'FeatureCollection', features: feats } });
        map.addLayer({ id: 'mat-cells-fill', type: 'fill', source: 'mat-cells-src', paint: {
          'fill-color': ['get', 'color'], 'fill-opacity': 0.85,
        } });
        map.addLayer({ id: 'mat-cells-line', type: 'line', source: 'mat-cells-src', paint: {
          'line-color': '#0F172A', 'line-width': 0.6, 'line-opacity': 0.7,
        } });
        // Track categories present in the focused rack so the legend
        // beneath the map can render coloured chips for the user (labels
        // were removed from the map itself to prevent overlap).
        const cats = Array.from(new Set(feats.map(f => (f.properties.category || '').toUpperCase()).filter(Boolean)))
          .map(c => ({ code: c, color: catColor[c] || '#94A3B8' }));
        setActiveCategories(cats);
        bindHandler('click', 'mat-cells-fill', (ev) => {
          const mf = ev.features?.[0]; if (!mf) return;
          // Filter the drawer to this materialCode AND make sure the
          // drawer is open (rack-focus alone doesn't guarantee the
          // WarehouseDrawer is mounted). Passing `focusedRack` as the
          // whDrawer key so the ledger scopes to that rack.
          setFocusedMaterial(mf.properties.materialCode);
          if (!whDrawer && focusedRack) {
            setWhDrawer(focusedRack);
            setDrawerSubtitle(`Rack · filter: ${mf.properties.materialCode}`);
          } else {
            setDrawerSubtitle(`Rack · filter: ${mf.properties.materialCode}`);
          }
        });
        bindHandler('mouseenter', 'mat-cells-fill', (ev) => {
          map.getCanvas().style.cursor = 'pointer';
          const mf = ev.features?.[0]; if (!mf) return;
          const mp = mf.properties;
          const poBadge = mp.poCount > 1 ? `<span style="margin-left:6px;padding:1px 4px;background:${mp.color};color:#fff;border-radius:3px;font-size:8px;font-weight:700">${mp.poCount} POs</span>` : '';
          showPopup(ev.lngLat, `<div style="min-width:180px;font-size:11px;font-family:'Helvetica Neue',sans-serif">
            <div style="font-weight:700;color:${mp.color}">${mp.materialCode}${poBadge}</div>
            <div style="color:#64748B;font-size:9px">${mp.description || ''}</div>
            <div style="color:#64748B;margin-top:2px">Stock: <b style="color:#10B981">${mp.stock} ${mp.unit || ''}</b></div>
            <div style="color:#F26B1F;font-size:9px;margin-top:2px">Click to filter · Issue MIR from drawer</div>
          </div>`);
        });
        bindHandler('mouseleave', 'mat-cells-fill', () => { map.getCanvas().style.cursor = ''; hidePopup(); });
      }
    }

    if (layers.coverageRings && snap.gateReaders?.length) {
      const mkCircle = (lng, lat, r) => {
        const pts = [];
        const latRad = (lat * Math.PI) / 180;
        const dLat = r / 111320;
        const dLng = r / (111320 * Math.cos(latRad || 1e-6));
        for (let i = 0; i <= 64; i++) {
          const a = (i / 64) * Math.PI * 2;
          pts.push([lng + dLng * Math.cos(a), lat + dLat * Math.sin(a)]);
        }
        return { type: 'Polygon', coordinates: [pts] };
      };
      const ringFeats = snap.gateReaders
        .filter(g => g.coverageRadiusMeters && g.gpsLat != null && g.gpsLng != null)
        .map(g => ({ type: 'Feature',
          properties: { id: g.id, r: g.coverageRadiusMeters, status: g.status },
          geometry: mkCircle(g.gpsLng, g.gpsLat, g.coverageRadiusMeters) }));
      if (ringFeats.length) {
        map.addSource('coverage-rings', { type: 'geojson',
          data: { type: 'FeatureCollection', features: ringFeats } });
        map.addLayer({ id: 'coverage-rings-fill', type: 'fill', source: 'coverage-rings', paint: {
          'fill-color': ['case', ['==', ['get', 'status'], 'ONLINE'], C.info, C.slateMuted],
          'fill-opacity': 0.10,
        } }, 'gate-pulse');
        map.addLayer({ id: 'coverage-rings-line', type: 'line', source: 'coverage-rings', paint: {
          'line-color': ['case', ['==', ['get', 'status'], 'ONLINE'], C.info, C.slateMuted],
          'line-width': 1, 'line-dasharray': [2, 2], 'line-opacity': 0.6,
        } }, 'gate-pulse');
      }
    }

    // ── Interactions ──────────────────────────────────────────────────
    const showPopup = (lngLat, html) => {
      if (popupRef.current) popupRef.current.remove();
      popupRef.current = new maplibregl.Popup({ closeButton: false, offset: 12, className: 'mtx-popup' })
        .setLngLat(lngLat).setHTML(html).addTo(map);
    };
    const hidePopup = () => { if (popupRef.current) { popupRef.current.remove(); popupRef.current = null; } };

    // Station polygon click → same drawer as bins (aggregated ledger)
    bindHandler('click', 'stations-fill', (e) => {
      const f = e.features?.[0]; if (!f) return;
      setWhDrawer(f.properties.location);
      setDrawerSubtitle('Site Station · On-Site Inventory');
      setHighlightUnit(null);
    });
    bindHandler('mouseenter', 'stations-fill', (e) => {
      map.getCanvas().style.cursor = 'pointer';
      const f = e.features?.[0]; if (!f) return;
      const p = f.properties;
      showPopup(e.lngLat, `<div style="min-width:180px;font-size:11px;font-family:'Helvetica Neue',sans-serif">
        <div style="font-weight:700;color:#0F766E">${p.label || p.location}</div>
        <div style="color:#64748B;margin-top:2px">On-site balance: <b style="color:#10B981">${p.balance ?? p.stock}</b> units</div>
        <div style="color:#F26B1F;font-size:9px;margin-top:2px">Click to open station ledger</div>
      </div>`);
    });
    bindHandler('mouseleave', 'stations-fill', () => { map.getCanvas().style.cursor = ''; hidePopup(); });

    // Bin click → aggregated bin ledger
    bindHandler('click', 'bins-fill', (e) => {
      const f = e.features?.[0]; if (!f) return;
      setWhDrawer(f.properties.location);
      setDrawerSubtitle('Bin · Aggregated IQC-Accepted Ledger');
      setHighlightUnit(null);
    });
    bindHandler('mouseenter', 'bins-fill', (e) => {
      map.getCanvas().style.cursor = 'pointer';
      const f = e.features?.[0]; if (!f) return;
      const p = f.properties;
      showPopup(e.lngLat, `<div style="min-width:180px;font-size:11px;font-family:'Helvetica Neue',sans-serif">
        <div style="font-weight:700;color:#1E4E8C">${p.label || p.location}</div>
        <div style="color:#64748B;margin-top:2px">Bin balance: <b style="color:#10B981">${p.balance ?? p.stock}</b> units</div>
        <div style="color:#F26B1F;font-size:9px;margin-top:2px">Click to open aggregated ledger</div>
      </div>`);
    });
    bindHandler('mouseleave', 'bins-fill', () => { map.getCanvas().style.cursor = ''; hidePopup(); });

    // Rack click → focus, fetch material sub-cells, open drawer
    bindHandler('click', 'racks-fill', async (e) => {
      const f = e.features?.[0]; if (!f) return;
      const rackLoc = f.properties.location;
      setWhDrawer(rackLoc);
      setDrawerSubtitle(`Rack · ${f.properties.discipline} · IQC-Accepted stock`);
      setHighlightUnit(null);
      setFocusedMaterial(null);
      setFocusedRack(rackLoc);
      // Fly-to the rack bounding box for context zoom
      try {
        const rack = (snap?.warehouses || []).find(w => w.location === rackLoc);
        if (rack && mapRef.current) {
          mapRef.current.flyTo({ center: [rack.gpsLng, rack.gpsLat], zoom: 18.2, duration: 900 });
        }
      } catch {}
      // Fetch materials on this rack (equal-sized sub-cells)
      try {
        const { data } = await api.get(
          `/v1/projects/${projectId}/mtx/warehouses/${encodeURIComponent(rackLoc)}/material-analysis`);
        setRackMaterials((data.materialCodes || []).filter(m => (m.finalInventory || m.iqcAccepted || 0) > 0));
      } catch { setRackMaterials([]); }
    });
    bindHandler('mouseenter', 'racks-fill', (e) => {
      map.getCanvas().style.cursor = 'pointer';
      const f = e.features?.[0]; if (!f) return;
      const p = f.properties;
      const statusColor = p.fillStatus === 'DANGER' ? '#EF4444'
                        : p.fillStatus === 'WARN'   ? '#F59E0B'
                        :                             '#10B981';
      const statusLabel = p.fillStatus === 'DANGER' ? 'OVERLOADED'
                        : p.fillStatus === 'WARN'   ? 'NEAR FULL'
                        :                             'OK';
      const fill = Number(p.fillPct || 0);
      // e.lngLat is the cursor position — safe for polygon features
      // (using f.geometry.coordinates crashes MapLibre with LngLatLike
      // error because a Polygon's coordinates are nested rings, not a
      // single [lng,lat] pair).
      showPopup(e.lngLat, `<div style="min-width:190px;font-size:11px;font-family:'Helvetica Neue',sans-serif">
        <div style="font-weight:700;color:${p.color}">${p.discipline} Rack · ${p.disciplineCode}</div>
        <div style="color:#64748B;font-size:9px">${p.parentBin}</div>
        <div style="color:#64748B;margin-top:2px">Balance: <b style="color:#10B981">${p.balance}</b> units</div>
        <div style="margin-top:4px">
          <div style="display:flex;align-items:center;gap:6px">
            <div style="flex:1;height:4px;border-radius:2px;background:#E2E8F0;overflow:hidden">
              <div style="width:${Math.min(100,fill)}%;height:100%;background:${statusColor}"></div>
            </div>
            <span style="font-family:monospace;font-weight:700;color:${statusColor};font-size:10px">${fill.toFixed(0)}%</span>
          </div>
          <div style="color:${statusColor};font-weight:700;font-size:9px;margin-top:2px">${statusLabel}${p.rackCapacity ? ` · cap ${p.rackCapacity} ${p.capacityUnit}` : ''}</div>
        </div>
        <div style="color:#F26B1F;font-size:9px;margin-top:4px">Click to open rack ledger</div>
      </div>`);
    });
    bindHandler('mouseleave', 'racks-fill', () => { map.getCanvas().style.cursor = ''; hidePopup(); });

    // Gate reader click
    bindHandler('click', 'gate-src', (e) => {
      const f = e.features?.[0]; if (!f) return; setGateDrawer(f.properties.id);
    });
    bindHandler('mouseenter', 'gate-src', (e) => {
      map.getCanvas().style.cursor = 'pointer';
      const f = e.features?.[0]; if (!f) return;
      const p = f.properties;
      const statusColor = p.status === 'ONLINE' ? '#10B981' : p.status === 'MISSING' ? '#EF4444' : '#F59E0B';
      showPopup(e.lngLat, `<div style="min-width:160px;font-size:11px;font-family:'Helvetica Neue',sans-serif">
        <div style="font-weight:700;color:#1E4E8C">${p.label || p.type}</div>
        <div style="color:#64748B">Type: <b style="color:#0F172A">${p.type}</b></div>
        <div style="color:#64748B">Status: <b style="color:${statusColor}">${p.status}</b></div>
        <div style="color:#64748B">Scans (${win}): <b style="color:#0F172A">${p.scans || 0}</b></div>
      </div>`);
    });
    bindHandler('mouseleave', 'gate-src', () => { map.getCanvas().style.cursor = ''; hidePopup(); });

    // Pick-on-map mode
    bindHandler('click', (e) => {
      if (!pickGpsCb) return;
      const feats = map.queryRenderedFeatures(e.point, { layers: ['bins-fill', 'racks-fill', 'gate-src'] });
      if (feats.length) return;
      pickGpsCb({ lat: e.lngLat.lat, lng: e.lngLat.lng });
      setPickGpsCb(null);
      map.getCanvas().style.cursor = '';
      toast.success('Coordinates picked ✓');
    });
  }, [snap, layers, boundary, refreshKey, win, pickGpsCb, focusedRack, rackMaterials]);

  const onUploadBoundary = async (file) => {
    if (!file) return;
    const text = await file.text();
    const format = file.name.toLowerCase().endsWith('.kml') ? 'kml' : 'geojson';
    try {
      await api.post(`/v1/projects/${projectId}/mtx/site-boundary`, { format, payload: text });
      toast.success(`Site boundary uploaded (${format.toUpperCase()})`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    }
  };

  const jumpToUnit = async (unit) => {
    setHighlightUnit(unit);
    setMaterialFilter('');
    setFilterMatches([]);
    // Find the rack this unit is stored on and fly to it
    const rack = (snap?.warehouses || []).find(w => w.type === 'RACK' && w.location === unit.currentBin);
    const target = rack || (snap?.warehouses || []).find(w => w.type === 'BIN');
    if (target && mapRef.current) {
      mapRef.current.flyTo({ center: [target.gpsLng, target.gpsLat], zoom: 17.5, duration: 900 });
      setWhDrawer(target.location);
      setDrawerSubtitle(rack ? `Rack · ${rack.discipline}` : 'Bin · Aggregated ledger');
    }
  };

  const k = snap?.kpis || {};
  const mapHeight = isFullscreen ? 'calc(100vh - 120px)' : 'calc(100vh - 340px)';

  return (
    <div className="p-3 flex gap-3" data-testid="site-map-tab" style={{ minHeight: 'calc(100vh - 160px)' }}>
      <style>{`.mtx-popup .maplibregl-popup-content { padding: 8px 10px; border-radius: 6px; box-shadow: 0 6px 18px rgba(15,23,42,0.15); }`}</style>
      <div className="flex-1 min-w-0 space-y-2">
        {/* Toolbar */}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex rounded border overflow-hidden" style={{ borderColor: C.border }}>
            {WINDOWS.map(w => (
              <button key={w.key} onClick={() => setWin(w.key)}
                className="px-2 py-1 text-[10px] font-semibold"
                data-testid={`sm-window-${w.key}`}
                style={{ background: win === w.key ? C.primary : '#fff', color: win === w.key ? '#fff' : C.slate }}>
                <Clock className="w-3 h-3 inline mr-0.5" /> {w.label}
              </button>
            ))}
          </div>
          <div className="flex rounded border overflow-hidden" style={{ borderColor: C.border }}>
            {[{k:'satellite',I:Satellite,l:'Satellite'},{k:'terrain',I:MapIcon,l:'Terrain'},
              {k:'voyager',I:MapIcon,l:'Voyager'},{k:'streets',I:MapIcon,l:'Streets'}].map(o => (
              <button key={o.k} onClick={() => setBaseStyle(o.k)}
                data-testid={`sm-base-${o.k}`}
                className="px-2 py-1 text-[10px] font-semibold flex items-center gap-1"
                style={{ background: baseStyle === o.k ? C.primary : '#fff',
                          color: baseStyle === o.k ? '#fff' : C.slate }}>
                <o.I className="w-3 h-3" /> {o.l}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-1 ml-1">
            <Layers className="w-3 h-3" style={{ color: C.slateMuted }} />
            {Object.keys(layers).map(l => (
              <button key={l} onClick={() => setLayers({ ...layers, [l]: !layers[l] })}
                className="px-1.5 py-0.5 text-[9px] rounded"
                data-testid={`sm-layer-${l}`}
                style={{
                  background: layers[l] ? '#EEF2FF' : '#fff',
                  border: `1px solid ${layers[l] ? C.primary : C.border}`,
                  color: layers[l] ? C.primary : C.slateMuted,
                }}>{l}</button>
            ))}
          </div>
          <div className="relative">
            <Search className="w-3 h-3 absolute left-2 top-1/2 -translate-y-1/2" style={{ color: C.slateMuted }} />
            <input value={materialFilter} onChange={e => setMaterialFilter(e.target.value)}
              placeholder="Filter by MCI / QR / RFID"
              data-testid="sm-material-filter"
              className="pl-7 pr-2 py-1 text-[10px] rounded border w-52"
              style={{ borderColor: C.border, background: '#fff', color: C.slate }} />
            {filterMatches.length > 0 && (
              <div className="absolute top-full left-0 mt-1 w-64 bg-white border rounded shadow-lg z-30"
                style={{ borderColor: C.border }}>
                {filterMatches.map((u, i) => (
                  <button key={i} onClick={() => jumpToUnit(u)}
                    data-testid={`sm-filter-match-${i}`}
                    className="block w-full text-left px-2 py-1 text-[10px] hover:bg-slate-50"
                    style={{ borderBottom: i < filterMatches.length - 1 ? `1px solid ${C.border}` : 'none' }}>
                    <div className="font-mono font-bold" style={{ color: C.primary }}>{u.id}</div>
                    <div className="truncate" style={{ color: C.slate }}>{u.materialDescription || u.mciLineNumber}</div>
                    <div style={{ color: C.slateMuted }}>State: {u.currentState}</div>
                  </button>
                ))}
              </div>
            )}
          </div>
          <Button size="sm" variant="ghost" onClick={() => fileRef.current?.click()}
            data-testid="sm-upload-boundary">
            <Upload className="w-3.5 h-3.5 mr-1" /> Boundary
          </Button>
          <input ref={fileRef} type="file" accept=".geojson,.json,.kml"
            className="hidden" onChange={e => onUploadBoundary(e.target.files?.[0])} />
          <Button size="sm" onClick={() => setAdminOpen(true)}
            data-testid="sm-admin-open"
            style={{ background: C.primary, color: '#fff' }}>
            <Settings className="w-3.5 h-3.5 mr-1" /> Manage Sites
          </Button>
          <Button size="sm" onClick={() => { setTransferFromLoc(''); setTransferOpen(true); }}
            data-testid="sm-transfer-open"
            style={{ background: C.accent, color: '#fff' }}>
            <ArrowRightLeft className="w-3.5 h-3.5 mr-1" /> Transfer
          </Button>
          <a href={`/mobile/scan?project=${projectId}`} target="_blank" rel="noreferrer"
            className="px-2 py-1 text-[10px] font-semibold rounded flex items-center gap-1"
            style={{ background: C.accent, color: '#fff' }}
            data-testid="sm-open-mobile-scan">
            <Camera className="w-3 h-3" /> Mobile Scan (PWA)
          </a>
          <Button size="sm" variant="ghost" onClick={() => setIsFullscreen(v => !v)}
            data-testid="sm-fullscreen">
            {isFullscreen ? <Minimize2 className="w-3.5 h-3.5" /> : <Maximize2 className="w-3.5 h-3.5" />}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => { load(); setRefreshKey(k => k + 1); }}
            data-testid="sm-refresh"><RefreshCw className="w-3.5 h-3.5" /></Button>
        </div>

        <HealthCheckBanner projectId={projectId} refreshKey={refreshKey} />

        {/* Map viewport */}
        <div className="rounded-lg border overflow-hidden bg-white" style={{ borderColor: C.border }}>
          <div ref={containerRef} style={{ height: mapHeight, minHeight: 480 }} data-testid="sm-viewport" />
          <div className="p-2 text-[9px] flex items-center gap-3 border-t flex-wrap" style={{ borderColor: C.border, background: '#F8FAFC' }}>
            <span style={{ color: C.accent }}>▬ ▬ Warehouse Zone</span>
            <span><span style={{ display:'inline-block',width:10,height:10,background:'#3B82F6',opacity:.5,borderRadius:2 }} /> Owner's Bin</span>
            <span><span style={{ display:'inline-block',width:10,height:10,background:'#8B5CF6',opacity:.5,borderRadius:2 }} /> Contractor's Bin</span>
            <span>● Rack pin (colored by discipline) — click for ledger</span>
            <span><Radio className="w-3 h-3 inline" style={{ color: C.success }} /> Gate reader</span>
            {activeCategories.length > 0 && (
              <span className="flex items-center gap-2 ml-2 pl-2 border-l" style={{ borderColor: C.border }}>
                <span className="uppercase font-semibold" style={{ color: C.slateMuted }}>Materials:</span>
                {activeCategories.map(c => (
                  <span key={c.code} className="flex items-center gap-1" data-testid={`sm-legend-mat-${c.code}`}>
                    <span style={{ display:'inline-block',width:10,height:10,background:c.color,borderRadius:2 }} />
                    <span style={{ color: C.slate }}>{c.code}</span>
                  </span>
                ))}
              </span>
            )}
          </div>
        </div>

        {/* 6 KPI band */}
        <div className="grid grid-cols-6 gap-2">
          <KpiTile testId="sm-kpi-wh"        label="Active Racks"               value={k.activeWarehouses ?? '—'} color={C.primary} />
          <KpiTile testId="sm-kpi-gates"     label="Gate Readers Online"        value={k.gateReadersOnline ?? '—'} color={C.success} />
          <KpiTile testId="sm-kpi-transit"   label="In-Transit Value"           value={k.inTransitValue ? `$${Math.round(k.inTransitValue).toLocaleString()}` : '$0'} color={C.accent} />
          <KpiTile testId="sm-kpi-scans"     label={`Scans (${win})`}           value={k.scanEventsInWindow ?? 0} color={C.info} />
          <KpiTile testId="sm-kpi-exc"       label="Open Exceptions"            value={k.openExceptions ?? 0} color={C.danger} />
          <KpiTile testId="sm-kpi-recon"     label="Reconciliation %"           value={`${k.reconciliationPct ?? 100}%`} color={(k.reconciliationPct ?? 100) >= 98 ? C.success : C.warn} />
        </div>
      </div>

      <CognaraPanel projectId={projectId} refreshKey={refreshKey} />

      {whDrawer && <WarehouseDrawer projectId={projectId} location={whDrawer}
        subtitle={drawerSubtitle} highlightUnit={highlightUnit}
        allWarehouses={snap?.warehouses || []}
        materialFilter={focusedMaterial || materialFilter}
        onIssueMir={(mat) => setIssueMirFor({ warehouseLocation: whDrawer, material: mat })}
        onTransfer={(loc) => { setTransferFromLoc(loc); setTransferOpen(true); }}
        onClose={() => { setWhDrawer(null); setDrawerSubtitle(null); setHighlightUnit(null);
                          setFocusedRack(null); setRackMaterials([]); setFocusedMaterial(null);
                          setActiveCategories([]); }} />}
      {issueMirFor && <IssueMIRFromMap projectId={projectId}
        warehouseLocation={issueMirFor.warehouseLocation}
        material={issueMirFor.material}
        onClose={() => setIssueMirFor(null)}
        onDone={() => { setIssueMirFor(null); load(); setRefreshKey(k => k + 1); }} />}
      {gateDrawer && <GateReaderDrawer projectId={projectId} ipId={gateDrawer} onClose={() => setGateDrawer(null)} />}
      {adminOpen && <SiteMapAdmin projectId={projectId}
        onClose={() => setAdminOpen(false)}
        onSaved={() => { load(); setRefreshKey(k => k + 1); }}
        onPickGps={(cb) => {
          setAdminOpen(false);
          setPickGpsCb(() => cb);
          if (mapRef.current) mapRef.current.getCanvas().style.cursor = 'crosshair';
          toast.info('Click any point on the map to set coordinates');
        }} />}
      {transferOpen && <BinTransferDialog projectId={projectId}
        defaultFromLocation={transferFromLoc}
        onClose={() => setTransferOpen(false)}
        onDone={() => { load(); setRefreshKey(k => k + 1); }} />}
      {pickGpsCb && (
        <div className="fixed top-20 left-1/2 -translate-x-1/2 z-50 px-3 py-1.5 rounded shadow-lg text-[11px] font-bold flex items-center gap-2"
          style={{ background: C.accent, color: '#fff' }}
          data-testid="sm-pick-mode-banner">
          <Crosshair className="w-3.5 h-3.5" /> Pick GPS mode — click any spot on the map
          <button onClick={() => { setPickGpsCb(null); setAdminOpen(true);
            if (mapRef.current) mapRef.current.getCanvas().style.cursor = ''; }}
            className="ml-2 underline">cancel</button>
        </div>
      )}
    </div>
  );
}
